package com.matchpredictor.web

import com.matchpredictor.web.api.ApiClient
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.events.Event
import kotlin.js.Date

private val scope = MainScope()

/** Скрыто, пока нет доверенного SSL: Web Push / Service Worker на self-signed не работают. */
private const val NOTIFICATIONS_UI_ENABLED = true

enum class Page { POLLS, CHAT, RATING, ACHIEVEMENTS, SETTINGS, ADMIN, AUTH, CHANGE_PASSWORD, RESET_PASSWORD }

object AppState {
    var user: UserDto? = null
    var page: Page = Page.POLLS
    var error: String? = null
    var notice: String? = null
    var selectedLeague: String = "EPL"
    var selectedRound: Int? = null
    var votedOnly: Boolean = false
    var closedOnly: Boolean = false
    var selectedRatingLeague: String? = null
    var selectedRatingRound: Int? = null
    var leagues: List<LeagueDto> = emptyList()
    var pollsCache: Map<String, List<PollDto>> = emptyMap()
    var ratingsCache: Map<String, List<RatingEntryDto>> = emptyMap()
    var confirmedPredictions: Map<Long, MatchOutcome> = emptyMap()
    var lineDataReady: Boolean = false
    var unreadNotifications: Long = 0
    var resetPasswordToken: String? = null
    var authForgotMode: Boolean = false
    /** After forgot-password request succeeds — show in-auth success panel. */
    var authForgotSuccessEmail: String? = null
}

/** Rebuilds the «Ближайший матч» slot after a vote updates pollsCache. */
private var refreshNextMatchWidget: (() -> Unit)? = null

fun main() {
    installButtonPressHighlight()
    registerServiceWorkerIfSupported()
    val root = document.getElementById("root") as? HTMLElement
    if (root == null) {
        console.error("root element not found")
        return
    }
    root.textContent = "Загрузка…"
    val params = window.location.search.removePrefix("?").split("&")
        .mapNotNull {
            val parts = it.split("=", limit = 2)
            if (parts.size == 2) parts[0] to decodeURIComponent(parts[1]) else null
        }.toMap()
    val verifyToken = params["verifyEmail"]
    val resetToken = params["resetPassword"]
    scope.launch {
        try {
            if (!verifyToken.isNullOrBlank()) {
                runCatching { ApiClient.verifyEmail(verifyToken) }
                    .onSuccess { AppState.notice = it.message }
                    .onFailure { AppState.error = errorMessage(it) }
                clearAuthQueryParams()
            }
            if (!resetToken.isNullOrBlank()) {
                AppState.resetPasswordToken = resetToken
                AppState.page = Page.RESET_PASSWORD
                clearAuthQueryParams()
                render()
                return@launch
            }
            if (ApiClient.token != null) {
                runCatching { AppState.user = ApiClient.me() }
                    .onFailure { ApiClient.token = null }
            }
            AppState.page = when {
                AppState.user == null -> Page.AUTH
                AppState.user?.mustChangePassword == true -> Page.CHANGE_PASSWORD
                else -> Page.POLLS
            }
            if (AppState.page == Page.POLLS) {
                enterAppWithBootLoader()
            } else {
                render()
            }
        } catch (t: Throwable) {
            console.error(t)
            root.innerHTML = ""
            root.appendChild(el("div", "banner error", "Ошибка запуска: ${t.message}"))
            root.appendChild(authPage())
        }
    }
}

private fun decodeURIComponent(value: String): String =
    window.asDynamic().decodeURIComponent(value) as String

private fun clearAuthQueryParams() {
    val url = window.location.pathname
    window.history.replaceState(null, "", url)
}

/** Registers sw.js early so the site is installable as PWA (home-screen icon). */
private fun registerServiceWorkerIfSupported() {
    val nav = window.navigator.asDynamic()
    if (nav.serviceWorker == null) return
    runCatching { nav.serviceWorker.register("/sw.js") }
        .onFailure { console.info("Service Worker registration skipped:", it.message) }
}

fun render() {
    PollCardCountdownViews.clearAll()
    val root = document.getElementById("root") as HTMLElement
    root.innerHTML = ""
    root.appendChild(layout())
}

/** Display-only countdown intervals; durable submission is owned by [PendingPredictions]. */
private object PollCardCountdownViews {
    private val byPollId = mutableMapOf<Long, Int>()

    fun set(pollId: Long, intervalId: Int) {
        clear(pollId)
        byPollId[pollId] = intervalId
    }

    fun clear(pollId: Long) {
        byPollId.remove(pollId)?.let { window.clearInterval(it) }
    }

    fun clearAll() {
        byPollId.keys.toList().forEach { clear(it) }
    }
}

private fun layout(): HTMLElement {
    val wrap = el("div", "app")
    val authLanding = AppState.page == Page.AUTH && AppState.user == null
    if (authLanding) wrap.classList.add("landing-app")
    if (!authLanding) wrap.appendChild(header())
    AppState.error?.let { wrap.appendChild(el("div", "banner error", it)) }
    AppState.notice?.let { wrap.appendChild(el("div", "banner ok", it)) }
    val content = el("main", "content")
    when (AppState.page) {
        Page.AUTH -> content.appendChild(authPage())
        Page.POLLS -> content.appendChild(pollsPage())
        Page.CHAT -> content.appendChild(chatPage())
        Page.RATING -> content.appendChild(ratingPage())
        Page.ACHIEVEMENTS -> content.appendChild(achievementsPage())
        Page.SETTINGS -> {
            if (NOTIFICATIONS_UI_ENABLED) {
                content.appendChild(settingsPage())
            } else {
                AppState.page = Page.POLLS
                content.appendChild(pollsPage())
            }
        }
        Page.ADMIN -> content.appendChild(adminPage())
        Page.CHANGE_PASSWORD -> content.appendChild(changeTemporaryPasswordPage())
        Page.RESET_PASSWORD -> content.appendChild(resetPasswordPage())
    }
    wrap.appendChild(content)
    when (AppState.page) {
        Page.POLLS, Page.ACHIEVEMENTS -> setViewportEdgeFadeEnabled(true)
        else -> setViewportEdgeFadeEnabled(false)
    }
    return wrap
}

private fun header(): HTMLElement {
    val header = el("header", "topbar")
    val identity = el("div", "topbar-identity")
    val brand = el("div", "brand")
    val brandMark = document.createElement("img") as HTMLImageElement
    brandMark.className = "brand-mark"
    brandMark.src = achievementImageUrl(DEFAULT_AVATAR_ICON)
    brandMark.alt = "Футбольные гуси"
    brand.appendChild(brandMark)
    brand.appendChild(el("div", "brand-text").also {
        it.appendChild(el("strong", text = "Футбольные гуси"))
    })
    identity.appendChild(brand)

    if (AppState.user != null) {
        val userBox = el("div", "userbox")
        userBox.appendChild(userBadge(AppState.user!!.displayName, AppState.user!!.avatarIcon))
        if (NOTIFICATIONS_UI_ENABLED && AppState.user?.mustChangePassword != true) {
            userBox.appendChild(settingsNavBtn())
            refreshUnreadBadge()
        }
        identity.appendChild(userBox)
    }
    header.appendChild(identity)

    if (AppState.user != null) {
        val navRow = el("div", "topbar-nav")
        if (AppState.user?.mustChangePassword != true) {
            val nav = el("nav", "nav")
            nav.appendChild(navBtn("Линия", Page.POLLS))
            nav.appendChild(navBtn("Чат", Page.CHAT))
            nav.appendChild(navBtn("Рейтинг", Page.RATING))
            nav.appendChild(navBtn("Ачивки", Page.ACHIEVEMENTS))
            if (AppState.user?.role == UserRole.ADMIN) {
                nav.appendChild(navBtn("Админ", Page.ADMIN))
            }
            navRow.appendChild(nav)
        }
        navRow.appendChild(logoutButton())
        header.appendChild(navRow)
    }
    return header
}

private fun logoutButton(): HTMLElement {
    return button("Выйти", "logout") {
        ChatStream.disconnect()
        AppState.user?.id?.let { PendingPredictions.clearForUser(it) }
        ApiClient.token = null
        AppState.user = null
        AppState.page = Page.AUTH
        window.sessionStorage.setItem("fg_just_logged_out", "1")
        window.sessionStorage.removeItem("fg_push_auto_prompted")
        clearSessionCaches()
        clearFlash()
        render()
    }
}

private fun navBtn(label: String, page: Page): HTMLElement {
    return button(label, if (AppState.page == page) "nav active" else "nav") {
        if (AppState.page == Page.CHAT && page != Page.CHAT) {
            ChatStream.disconnect()
        }
        AppState.page = page
        clearFlash()
        render()
    }
}

private fun settingsNavBtn(): HTMLElement {
    val count = AppState.unreadNotifications
    val btn = el("button", if (AppState.page == Page.SETTINGS) "nav active nav-settings" else "nav nav-settings")
    btn.setAttribute("type", "button")
    btn.setAttribute("aria-label", if (count > 0) "Настройки, непрочитанных: $count" else "Настройки")
    btn.setAttribute("title", "Настройки")
    btn.innerHTML =
        """<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.48.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87a.48.48 0 0 0 .12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.03-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/>
          </svg>"""
    if (count > 0) {
        btn.appendChild(el("span", "nav-badge", if (count > 99) "99+" else count.toString()))
    }
    btn.addEventListener("click", {
        if (AppState.page == Page.CHAT) ChatStream.disconnect()
        AppState.page = Page.SETTINGS
        clearFlash()
        render()
    })
    return btn
}

private fun refreshUnreadBadge() {
    if (AppState.user == null || AppState.user?.mustChangePassword == true) return
    scope.launch {
        runCatching {
            AppState.unreadNotifications = ApiClient.unreadNotificationCount().count
        }
    }
}

private const val PASSWORD_REQUIREMENTS =
    "Минимум 6 символов и минимум 2 категории: строчные буквы, заглавные буквы, цифры, спецсимволы."

private fun passwordPolicyError(password: String): String? {
    if (password.length < 6) return "Пароль должен содержать не менее 6 символов"
    val categories = listOf(
        password.any { it.isLowerCase() },
        password.any { it.isUpperCase() },
        password.any { it.isDigit() },
        password.any { !it.isLetterOrDigit() && !it.isWhitespace() }
    ).count { it }
    return if (categories < 2) "Используйте в пароле минимум 2 категории символов" else null
}

private fun landingHero(): HTMLElement {
    val hero = el("div", "landing-hero")
    val mark = document.createElement("img") as HTMLImageElement
    mark.className = "landing-mark"
    mark.src = achievementImageUrl(DEFAULT_AVATAR_ICON)
    mark.alt = "Футбольные гуси"
    hero.appendChild(mark)
    hero.appendChild(el("h1", "landing-title", "Футбольные гуси"))
    hero.appendChild(
        el("p", "landing-tag", "Прогнозы на топ-лиги. Один голос — один счёт.")
    )
    val points = el("ul", "landing-points")
    listOf("Линия матчей", "Рейтинг сезона", "Ачивки").forEach { label ->
        points.appendChild(el("li", "landing-point", label))
    }
    hero.appendChild(points)
    return hero
}

private fun authPage(): HTMLElement {
    val page = el("div", "landing")
    page.appendChild(landingHero())
    val box = el("section", "auth-card")
    val title = el("h2", "auth-heading", "Вход")
    title.hidden = true
    val subtitle = el("p", "lede", "Один аккаунт — один голос. Без накруток.")
    box.appendChild(title)
    box.appendChild(subtitle)

    val tabs = el("div", "auth-tabs")
    tabs.setAttribute("role", "tablist")
    val loginTab = el("button", "auth-tab active", "Вход")
    loginTab.setAttribute("type", "button")
    loginTab.setAttribute("role", "tab")
    loginTab.setAttribute("aria-selected", "true")
    val registerTab = el("button", "auth-tab", "Регистрация")
    registerTab.setAttribute("type", "button")
    registerTab.setAttribute("role", "tab")
    registerTab.setAttribute("aria-selected", "false")
    tabs.appendChild(loginTab)
    tabs.appendChild(registerTab)
    box.appendChild(tabs)

    val form = document.createElement("form") as HTMLFormElement
    form.setAttribute("autocomplete", "on")
    form.noValidate = true
    var registerMode = false

    val justLoggedOut = window.sessionStorage.getItem("fg_just_logged_out") == "1"
    val user = input("text", "Логин")
    user.id = "auth-username"
    user.setAttribute("autocomplete", "username")
    user.name = "username"

    val pass = input("password", "Пароль")
    pass.id = "auth-password"
    pass.name = "password"
    pass.value = ""
    if (justLoggedOut) {
        pass.setAttribute("autocomplete", "off")
        pass.addEventListener("focus", {
            if (!registerMode) pass.setAttribute("autocomplete", "current-password")
        })
    } else {
        pass.setAttribute("autocomplete", "current-password")
    }

    val revealButton = el("button", "password-reveal")
    revealButton.setAttribute("type", "button")
    revealButton.setAttribute("aria-label", "Показать пароль, пока кнопка удерживается")
    revealButton.setAttribute("aria-pressed", "false")
    fun setRevealIcon(open: Boolean) {
        if (open) {
            revealButton.textContent = "👁"
        } else {
            revealButton.innerHTML =
                """<img class="password-reveal-icon" src="icons/eye-closed.png" alt="" draggable="false"/>"""
        }
    }
    fun showPassword() {
        pass.type = "text"
        revealButton.classList.add("active")
        revealButton.setAttribute("aria-pressed", "true")
        setRevealIcon(open = true)
    }
    fun hidePassword() {
        pass.type = "password"
        revealButton.classList.remove("active")
        revealButton.setAttribute("aria-pressed", "false")
        setRevealIcon(open = false)
    }
    setRevealIcon(open = false)
    revealButton.addEventListener("pointerdown", { event ->
        (event as Event).preventDefault()
        showPassword()
    })
    listOf("pointerup", "pointercancel", "pointerleave", "blur").forEach { eventName ->
        revealButton.addEventListener(eventName, { hidePassword() })
    }
    revealButton.addEventListener("keydown", { event ->
        val key = event.asDynamic().key as? String
        if (key == " " || key == "Enter") {
            (event as Event).preventDefault()
            showPassword()
        }
    })
    revealButton.addEventListener("keyup", { hidePassword() })

    val infoButton = el("button", "password-info", "i")
    infoButton.setAttribute("type", "button")
    infoButton.setAttribute("aria-label", "Требования к паролю")
    infoButton.setAttribute("aria-expanded", "false")
    val requirements = el("p", "password-requirements", PASSWORD_REQUIREMENTS)
    requirements.setAttribute("hidden", "")
    var requirementsVisible = false
    infoButton.addEventListener("click", {
        requirementsVisible = !requirementsVisible
        infoButton.setAttribute("aria-expanded", requirementsVisible.toString())
        if (requirementsVisible) requirements.removeAttribute("hidden")
        else requirements.setAttribute("hidden", "")
    })

    val passwordField = el("div", "field")
    val passwordTitle = el("div", "field-title")
    val passwordLabel = el("label", text = "Пароль")
    passwordLabel.setAttribute("for", pass.id)
    passwordTitle.appendChild(passwordLabel)
    passwordTitle.appendChild(infoButton)
    val passwordInput = el("div", "password-input")
    passwordInput.appendChild(pass)
    passwordInput.appendChild(revealButton)
    passwordField.appendChild(passwordTitle)
    passwordField.appendChild(passwordInput)
    passwordField.appendChild(requirements)

    val name = input("text", "Имя")
    name.setAttribute("autocomplete", "nickname")
    val nameField = field("Отображаемое имя", name)
    nameField.style.display = "none"

    val email = input("email", "Email")
    email.id = "auth-email"
    email.setAttribute("autocomplete", "email")
    email.name = "email"
    val emailField = field("Email", email)
    emailField.style.display = "none"

    val remember = document.createElement("input") as HTMLInputElement
    remember.type = "checkbox"
    remember.id = "remember-login"
    remember.checked = RememberedAuth.rememberEnabled
    val rememberLabel = el("label", "remember")
    rememberLabel.appendChild(remember)
    rememberLabel.appendChild(el("span", text = "Запомнить логин (пароль — в менеджере браузера)"))
    val formError = el("p", "form-error")

    val forgotLink = el("button", "link-btn", "Забыли пароль?")
    forgotLink.setAttribute("type", "button")

    form.appendChild(field("Логин", user))
    form.appendChild(passwordField)
    form.appendChild(nameField)
    form.appendChild(emailField)
    form.appendChild(rememberLabel)
    form.appendChild(forgotLink)
    RememberedAuth.loadInto(user)
    remember.checked = RememberedAuth.rememberEnabled

    fun shakeFields(fields: List<HTMLInputElement>) {
        val uniqueFields = fields.distinct()
        uniqueFields.forEach { input ->
            input.classList.remove("auth-input-shake")
            input.asDynamic().offsetWidth
            input.classList.add("auth-input-shake")
        }
        window.setTimeout({
            uniqueFields.forEach { it.classList.remove("auth-input-shake") }
        }, 1200)
    }

    fun showFormError(message: String, fields: List<HTMLInputElement>) {
        formError.textContent = message
        shakeFields(fields)
    }

    fun submitLogin() {
        pass.setAttribute("autocomplete", "current-password")
        val emptyFields = buildList {
            if (user.value.isBlank()) add(user)
            if (pass.value.isBlank()) add(pass)
        }
        if (emptyFields.isNotEmpty()) {
            val message = when {
                user.value.isBlank() && pass.value.isBlank() -> "Введите логин и пароль"
                user.value.isBlank() -> "Введите логин"
                else -> "Введите пароль"
            }
            showFormError(message, emptyFields)
            if (pass.value.isBlank()) pass.asDynamic().focus()
            return
        }
        scope.launch {
            runCatching {
                val auth = ApiClient.login(user.value.trim(), pass.value)
                RememberedAuth.save(user.value.trim(), remember.checked)
                ApiClient.token = auth.token
                AppState.user = auth.user
                AppState.page =
                    if (auth.user.mustChangePassword) Page.CHANGE_PASSWORD else Page.POLLS
                AppState.notice =
                    if (auth.user.mustChangePassword) "Установите новый постоянный пароль"
                    else "С возвращением, ${auth.user.displayName}"
                AppState.error = null
                window.sessionStorage.removeItem("fg_just_logged_out")
            }.onFailure {
                showFormError(errorMessage(it), listOf(user, pass))
                return@launch
            }
            if (AppState.page == Page.POLLS) {
                enterAppWithBootLoader()
            } else {
                render()
            }
        }
    }

    fun submitRegister() {
        window.sessionStorage.removeItem("fg_just_logged_out")
        pass.setAttribute("autocomplete", "new-password")

        val errors = mutableListOf<String>()
        val invalidFields = mutableListOf<HTMLInputElement>()
        when {
            user.value.isBlank() -> {
                errors += "Введите логин"
                invalidFields += user
            }
            user.value.trim().length < 3 -> {
                errors += "Логин должен содержать не менее 3 символов"
                invalidFields += user
            }
        }
        if (pass.value.isBlank()) {
            errors += "Введите пароль"
            invalidFields += pass
        } else {
            passwordPolicyError(pass.value)?.let {
                errors += it
                invalidFields += pass
            }
        }
        if (name.value.isBlank()) {
            errors += "Введите отображаемое имя"
            invalidFields += name
        }
        when {
            email.value.isBlank() -> {
                errors += "Введите email"
                invalidFields += email
            }
            !email.value.trim().contains("@") -> {
                errors += "Укажите корректный email"
                invalidFields += email
            }
        }
        if (errors.isNotEmpty()) {
            showFormError(errors.joinToString(". "), invalidFields)
            return
        }

        scope.launch {
            runCatching {
                val auth = ApiClient.register(
                    user.value.trim(),
                    pass.value,
                    name.value.trim(),
                    email.value.trim()
                )
                RememberedAuth.save(user.value.trim(), remember.checked)
                ApiClient.token = auth.token
                AppState.user = auth.user
                AppState.page = Page.POLLS
                AppState.notice =
                    "Аккаунт создан. Подтвердите email по ссылке из письма — если письма нет, загляните в «Спам»."
                AppState.error = null
            }.onFailure {
                val message = errorMessage(it)
                val fields = when {
                    message.contains("логин", ignoreCase = true) -> listOf(user)
                    message.contains("парол", ignoreCase = true) -> listOf(pass)
                    message.contains("имя", ignoreCase = true) -> listOf(name)
                    message.contains("email", ignoreCase = true) -> listOf(email)
                    else -> emptyList()
                }
                showFormError(message, fields)
                return@launch
            }
            enterAppWithBootLoader()
        }
    }

    val actions = el("div", "actions")
    val submitButton = document.createElement("button") as HTMLButtonElement
    submitButton.className = "primary"
    submitButton.textContent = "Войти"
    submitButton.type = "submit"
    actions.appendChild(submitButton)
    form.appendChild(actions)
    form.appendChild(formError)

    val forgotProgress = el("div", "mail-progress")
    forgotProgress.hidden = true
    forgotProgress.appendChild(el("div", "mail-spinner").also { it.setAttribute("aria-hidden", "true") })
    val forgotProgressTitle = el("p", "mail-progress-title", "Отправляем ссылку…")
    forgotProgress.appendChild(forgotProgressTitle)
    forgotProgress.appendChild(el("p", "muted", "Обычно это занимает несколько секунд."))

    val forgotSuccess = el("div", "mail-success")
    forgotSuccess.hidden = true
    val forgotSuccessTitle = el("p", "mail-success-title", "Проверьте почту")
    val forgotSuccessBody = el("div", "mail-success-body")
    forgotSuccess.appendChild(forgotSuccessTitle)
    forgotSuccess.appendChild(forgotSuccessBody)
    val forgotSuccessActions = el("div", "actions")
    forgotSuccess.appendChild(forgotSuccessActions)

    fun setAuthBusy(busy: Boolean, label: String = "Отправляем…") {
        forgotProgress.hidden = !busy
        form.hidden = busy
        forgotSuccess.hidden = true
        if (busy) {
            forgotProgressTitle.textContent = label
            tabs.style.display = "none"
            title.textContent = "Восстановление пароля"
            subtitle.textContent = ""
        }
        submitButton.disabled = busy
        email.disabled = busy
        user.disabled = busy
        pass.disabled = busy
    }

    fun setForgotMode(enabled: Boolean) {
        AppState.authForgotMode = enabled
        if (enabled) AppState.authForgotSuccessEmail = null
        registerMode = false
        forgotProgress.hidden = true
        forgotSuccess.hidden = true
        form.hidden = false
        tabs.style.display = if (enabled) "none" else "flex"
        title.hidden = !enabled
        title.textContent = if (enabled) "Восстановление пароля" else "Вход"
        subtitle.textContent =
            if (enabled) "Укажите подтверждённый email — пришлём ссылку для сброса."
            else "Один аккаунт — один голос. Без накруток."
        passwordField.style.display = if (enabled) "none" else "block"
        nameField.style.display = "none"
        emailField.style.display = if (enabled) "flex" else "none"
        (user.parentElement as? HTMLElement)?.style?.display = if (enabled) "none" else "flex"
        rememberLabel.style.display = if (enabled) "none" else "flex"
        forgotLink.style.display = if (enabled) "none" else "inline"
        submitButton.textContent = if (enabled) "Отправить ссылку" else "Войти"
        submitButton.disabled = false
        formError.textContent = ""
        form.querySelector("#forgot-back")?.remove()
        if (enabled) {
            val back = el("button", "link-btn", "Вернуться назад")
            back.setAttribute("type", "button")
            back.id = "forgot-back"
            back.addEventListener("click", { setForgotMode(false) })
            form.insertBefore(back, actions)
        }
    }

    fun showForgotSuccess(sentTo: String) {
        AppState.authForgotSuccessEmail = sentTo
        AppState.authForgotMode = false
        AppState.notice = null
        AppState.error = null
        forgotProgress.hidden = true
        form.hidden = true
        tabs.style.display = "none"
        forgotSuccess.hidden = false
        title.hidden = false
        title.textContent = "Проверьте почту"
        subtitle.textContent = ""
        submitButton.disabled = false
        email.disabled = false
        user.disabled = false
        pass.disabled = false
        forgotSuccessBody.innerHTML = ""
        forgotSuccessBody.appendChild(
            el(
                "p",
                text = "Если аккаунт с адресом $sentTo существует и почта подтверждена, мы отправим ссылку для сброса пароля."
            )
        )
        forgotSuccessBody.appendChild(
            el(
                "p",
                "muted",
                "Письмо может прийти с задержкой. Загляните также в «Спам» и «Промоакции»."
            )
        )
        forgotSuccessActions.innerHTML = ""
        forgotSuccessActions.appendChild(button("Вернуться ко входу", "primary") {
            AppState.authForgotSuccessEmail = null
            forgotSuccess.hidden = true
            form.hidden = false
            setForgotMode(false)
        })
    }

    fun submitForgot() {
        if (email.value.isBlank()) {
            showFormError("Введите email", listOf(email))
            return
        }
        val targetEmail = email.value.trim()
        scope.launch {
            setAuthBusy(true, "Отправляем ссылку для сброса…")
            runCatching {
                ApiClient.forgotPassword(targetEmail)
                showForgotSuccess(targetEmail)
            }.onFailure {
                setAuthBusy(false)
                showFormError(errorMessage(it), listOf(email))
            }
        }
    }

    fun setRegisterMode(enabled: Boolean) {
        if (AppState.authForgotMode) setForgotMode(false)
        AppState.authForgotSuccessEmail = null
        forgotSuccess.hidden = true
        forgotProgress.hidden = true
        form.hidden = false
        registerMode = enabled
        title.hidden = true
        title.textContent = if (enabled) "Регистрация" else "Вход"
        subtitle.textContent =
            if (enabled) "Создай аккаунт и присоединяйся к сезону."
            else "Один аккаунт — один голос. Без накруток."
        loginTab.className = if (enabled) "auth-tab" else "auth-tab active"
        registerTab.className = if (enabled) "auth-tab active" else "auth-tab"
        loginTab.setAttribute("aria-selected", (!enabled).toString())
        registerTab.setAttribute("aria-selected", enabled.toString())
        nameField.style.display = if (enabled) "flex" else "none"
        emailField.style.display = if (enabled) "flex" else "none"
        infoButton.style.display = if (enabled) "inline-grid" else "none"
        forgotLink.style.display = if (enabled) "none" else "inline"
        tabs.style.display = "flex"
        if (!enabled) {
            requirementsVisible = false
            requirements.setAttribute("hidden", "")
            infoButton.setAttribute("aria-expanded", "false")
        }
        pass.setAttribute("autocomplete", if (enabled) "new-password" else "current-password")
        submitButton.textContent = if (enabled) "Создать аккаунт" else "Войти"
        formError.textContent = ""
    }

    forgotLink.addEventListener("click", { setForgotMode(true) })
    loginTab.addEventListener("click", { afterPressHighlightFade { setRegisterMode(false) } })
    registerTab.addEventListener("click", { afterPressHighlightFade { setRegisterMode(true) } })
    form.addEventListener("submit", { event ->
        (event as Event).preventDefault()
        when {
            AppState.authForgotMode -> submitForgot()
            registerMode -> submitRegister()
            else -> submitLogin()
        }
    })
    setRegisterMode(false)
    when {
        !AppState.authForgotSuccessEmail.isNullOrBlank() -> showForgotSuccess(AppState.authForgotSuccessEmail!!)
        AppState.authForgotMode -> setForgotMode(true)
    }
    box.appendChild(form)
    box.appendChild(forgotProgress)
    box.appendChild(forgotSuccess)
    page.appendChild(box)
    return page
}

private fun resetPasswordPage(): HTMLElement {
    val box = el("section", "auth-card")
    box.appendChild(el("h1", text = "Новый пароль"))
    box.appendChild(el("p", "lede", "Задайте новый пароль для входа."))
    val form = document.createElement("form") as HTMLFormElement
    form.noValidate = true
    val newPassword = input("password", "Новый пароль")
    newPassword.setAttribute("autocomplete", "new-password")
    val confirmation = input("password", "Повторите пароль")
    confirmation.setAttribute("autocomplete", "new-password")
    val formError = el("p", "form-error")
    form.appendChild(field("Новый пароль", newPassword))
    form.appendChild(field("Повтор", confirmation))
    val submit = el("button", "primary", "Сохранить пароль")
    submit.setAttribute("type", "submit")
    form.appendChild(el("div", "actions").also { it.appendChild(submit) })
    form.appendChild(formError)
    form.addEventListener("submit", { event ->
        (event as Event).preventDefault()
        val token = AppState.resetPasswordToken
        if (token.isNullOrBlank()) {
            formError.textContent = "Ссылка сброса недействительна"
            return@addEventListener
        }
        val policyError = passwordPolicyError(newPassword.value)
        when {
            policyError != null -> formError.textContent = policyError
            newPassword.value != confirmation.value -> formError.textContent = "Пароли не совпадают"
            else -> scope.launch {
                runCatching {
                    val res = ApiClient.resetPassword(token, newPassword.value)
                    AppState.resetPasswordToken = null
                    AppState.notice = res.message
                    AppState.page = Page.AUTH
                    ApiClient.token = null
                    render()
                }.onFailure {
                    formError.textContent = errorMessage(it)
                }
            }
        }
    })
    box.appendChild(form)
    return box
}

private fun changeTemporaryPasswordPage(): HTMLElement {
    val box = el("section", "auth-card")
    box.appendChild(el("h1", text = "Установите новый пароль"))
    box.appendChild(
        el(
            "p",
            "lede",
            "Администратор выдал временный пароль. Перед продолжением задайте свой постоянный пароль."
        )
    )

    val form = document.createElement("form") as HTMLFormElement
    form.noValidate = true
    val newPassword = input("password", "Новый пароль")
    newPassword.name = "new-password"
    newPassword.setAttribute("autocomplete", "new-password")
    val confirmation = input("password", "Повторите новый пароль")
    confirmation.name = "new-password-confirmation"
    confirmation.setAttribute("autocomplete", "new-password")
    val formError = el("p", "form-error")

    fun submit() {
        val policyError = passwordPolicyError(newPassword.value)
        when {
            policyError != null -> {
                formError.textContent = policyError
                return
            }
            newPassword.value != confirmation.value -> {
                formError.textContent = "Пароли не совпадают"
                confirmation.value = ""
                return
            }
        }
        scope.launch {
            runCatching {
                val auth = ApiClient.changeTemporaryPassword(newPassword.value)
                ApiClient.token = auth.token
                AppState.user = auth.user
                AppState.page = Page.POLLS
                AppState.notice = "Новый пароль сохранён"
                AppState.error = null
            }.onFailure {
                formError.textContent = errorMessage(it)
                newPassword.value = ""
                confirmation.value = ""
                return@launch
            }
            enterAppWithBootLoader()
        }
    }

    form.appendChild(field("Новый пароль", newPassword))
    form.appendChild(field("Повторите пароль", confirmation))
    form.appendChild(el("p", "password-requirements", PASSWORD_REQUIREMENTS))
    val savePasswordButton = el("button", "primary", "Сохранить пароль")
    savePasswordButton.setAttribute("type", "submit")
    form.appendChild(savePasswordButton)
    form.appendChild(formError)
    form.addEventListener("submit", { event ->
        (event as Event).preventDefault()
        submit()
    })
    box.appendChild(form)
    return box
}

private fun scrollLeagueTabIntoView(tabs: HTMLElement, code: String) {
    val nodes = tabs.querySelectorAll("button.league-tab")
    for (i in 0 until nodes.length) {
        val btn = nodes.item(i) as? HTMLElement ?: continue
        if (btn.getAttribute("data-league").orEmpty() == code) {
            btn.asDynamic().scrollIntoView(
                js("{ inline: 'center', block: 'nearest', behavior: 'smooth' }")
            )
            return
        }
    }
}

/** Плавное затухание чипов у краёв: ширина mask растёт вместе со scrollLeft. */
private fun bindLeagueTabsEdgeFade(tabs: HTMLElement) {
    bindScrollEdgeFade(tabs, EdgeFadeAxis.HORIZONTAL, fadeMaxPx = 96.0)
}

private fun nearestVotablePoll(): PollDto? {
    val now = (js("Date.now()") as Double).toLong()
    val open = AppState.pollsCache.values.flatten().filter { poll ->
        poll.status == PollStatus.OPEN && voteDeadlineMillis(poll) > now
    }
    if (open.isEmpty()) return null
    val leaguePool = open.filter { it.league == League.RPL }.ifEmpty { open }
    val currentRound = leaguePool.mapNotNull { roundNumber(it.roundName) }.minOrNull()
    val inCurrentRound = if (currentRound == null) {
        leaguePool
    } else {
        leaguePool.filter { roundNumber(it.roundName) == currentRound }
    }
    return inCurrentRound
        .filter { predictionForPoll(it) == null }
        .minByOrNull { kickoffMillis(it.kickoffAt) }
}

private fun predictionForPoll(poll: PollDto): MatchOutcome? {
    poll.myPrediction?.let { return it }
    AppState.confirmedPredictions[poll.id]?.let { return it }
    val userId = AppState.user?.id ?: return null
    return PendingPredictions.get(userId, poll.id)?.choice
}

private fun voteDeadlineMillis(poll: PollDto): Long {
    val kickoff = kickoffMillis(poll.kickoffAt)
    val closes = kickoffMillis(poll.closesAt)
    return if (closes in 1 until kickoff) closes else kickoff
}

private fun nextMatchCard(poll: PollDto, onOpen: (PollDto) -> Unit): HTMLElement {
    val btn = el("button", "next-match")
    btn.setAttribute("type", "button")
    val leagueTitle = AppState.leagues.firstOrNull { it.code == poll.league.name }?.title
        ?: poll.league.name
    btn.appendChild(el("span", "next-match-kicker", "Ближайший матч"))
    val row = el("div", "next-match-row")
    row.appendChild(teamSide(poll.homeTeam, poll.homeLogoUrl))
    row.appendChild(el("span", "matchup-vs", "—"))
    row.appendChild(teamSide(poll.awayTeam, poll.awayLogoUrl))
    btn.appendChild(row)
    btn.appendChild(
        el(
            "p",
            "next-match-meta",
            "$leagueTitle · ${formatRoundLabel(poll.roundName)} · ${formatDateTime(poll.kickoffAt)}"
        )
    )
    btn.addEventListener("click", {
        afterPressHighlightFade { onOpen(poll) }
    })
    return btn
}

private fun pollsPage(): HTMLElement {
    val section = el("section", "page")
    val nextMatchSlot = el("div", "next-match-slot")
    val tabs = el("div", "league-tabs")
    val subtitle = el("p", "league-sub", "")
    val filters = el("div", "poll-filters")
    val list = el("div", "poll-list")
    section.appendChild(nextMatchSlot)
    section.appendChild(el("h1", text = "Линия"))
    section.appendChild(el("p", "lede", "Выбери лигу и исход матча: победа хозяев, ничья или победа гостей."))
    section.appendChild(tabs)
    section.appendChild(subtitle)
    section.appendChild(filters)
    section.appendChild(list)

    fun syncLeagueTabActive() {
        val selected = AppState.selectedLeague
        val nodes = tabs.querySelectorAll("button.league-tab")
        for (i in 0 until nodes.length) {
            val btn = nodes.item(i) as? HTMLElement ?: continue
            if (btn.getAttribute("data-league") == selected) {
                btn.classList.add("active")
            } else {
                btn.classList.remove("active")
            }
        }
        val current = AppState.leagues.firstOrNull { it.code == selected }
        subtitle.textContent = current?.subtitle ?: ""
        scrollLeagueTabIntoView(tabs, selected)
        bindLeagueTabsEdgeFade(tabs)
    }

    var paintBodyFn: () -> Unit = {}

    fun openNearestMatch(poll: PollDto) {
        AppState.selectedLeague = poll.league.name
        AppState.selectedRound = roundNumber(poll.roundName)
        AppState.votedOnly = false
        AppState.closedOnly = false
        clearFlash()
        syncLeagueTabActive()
        paintBodyFn()
        window.setTimeout({
            val target = list.querySelector("[data-poll-id='${poll.id}']") as? HTMLElement ?: return@setTimeout
            target.asDynamic().scrollIntoView(
                js("{ block: 'center', behavior: 'smooth' }")
            )
            target.classList.add("poll-flash")
            window.setTimeout({ target.classList.remove("poll-flash") }, 1600)
        }, 50)
    }

    fun paintNextMatch() {
        nextMatchSlot.innerHTML = ""
        val poll = nearestVotablePoll() ?: return
        nextMatchSlot.appendChild(nextMatchCard(poll) { openNearestMatch(it) })
    }

    fun paintBody() {
        val polls = AppState.pollsCache[AppState.selectedLeague].orEmpty()
            .sortedWith(compareBy<PollDto> { roundSortKey(it.roundName) }.thenBy { it.kickoffAt })
        val pollsByRound = polls.groupBy { roundNumber(it.roundName) }
        val availableRounds = pollsByRound.keys.filterNotNull().sorted()
        val archivedRounds = availableRounds.filter { round ->
            isRoundArchived(pollsByRound[round].orEmpty())
        }.toSet()

        filters.innerHTML = ""
        val roundSelect = document.createElement("select") as HTMLSelectElement
        roundSelect.className = "round-filter-select"
        roundSelect.setAttribute("aria-label", "Выбор тура")
        roundSelect.innerHTML = "<option value=\"\">Все туры</option>" +
            availableRounds.joinToString("") { round ->
                val suffix = if (round in archivedRounds) " (завершён)" else ""
                "<option value=\"$round\">Тур $round$suffix</option>"
            }
        roundSelect.value = AppState.selectedRound?.toString().orEmpty()
        roundSelect.addEventListener("change", {
            AppState.selectedRound = roundSelect.value.toIntOrNull()
            clearFlash()
            paintBody()
        })
        filters.appendChild(roundSelect)
        filters.appendChild(
            button(
                "Проголосованные",
                if (AppState.votedOnly) "filter-chip active" else "filter-chip"
            ) {
                AppState.votedOnly = !AppState.votedOnly
                clearFlash()
                paintBody()
            }
        )
        filters.appendChild(
            button(
                "Закрытые",
                if (AppState.closedOnly) "filter-chip active" else "filter-chip"
            ) {
                AppState.closedOnly = !AppState.closedOnly
                clearFlash()
                paintBody()
            }
        )

        val visiblePolls = polls
            .filter { AppState.selectedRound == null || roundNumber(it.roundName) == AppState.selectedRound }
            .filter { selected ->
                // «Все туры»: скрываем завершённые туры; выбранный тур всегда виден.
                AppState.selectedRound != null ||
                    roundNumber(selected.roundName)?.let { it !in archivedRounds } != false
            }
            .filter { !AppState.votedOnly || predictionForPoll(it) != null }
            .filter { !AppState.closedOnly || it.status != PollStatus.OPEN }

        PollCardCountdownViews.clearAll()
        list.innerHTML = ""
        paintNextMatch()
        if (polls.isEmpty()) {
            list.appendChild(
                el(
                    "p",
                    "empty",
                    "В этой лиге пока нет опросов. Админ может нажать «Синхронизировать матчи»."
                )
            )
        } else if (visiblePolls.isEmpty()) {
            list.appendChild(el("p", "empty", "Нет событий, соответствующих выбранным фильтрам."))
        } else {
            var lastRound: String? = null
            visiblePolls.forEach { poll ->
                val roundKey = poll.roundName ?: ""
                if (roundKey != lastRound) {
                    lastRound = roundKey
                    list.appendChild(el("h2", "round-heading", formatRoundLabel(poll.roundName)))
                }
                list.appendChild(pollCard(poll))
            }
        }
    }

    paintBodyFn = { paintBody() }

    fun ensureLeagueTabs() {
        val leagues = AppState.leagues
        val codes = leagues.map { it.code }
        val existing = mutableListOf<String>()
        val nodes = tabs.querySelectorAll("button.league-tab")
        for (i in 0 until nodes.length) {
            val code = (nodes.item(i) as? HTMLElement)?.getAttribute("data-league")
            if (code != null) existing.add(code)
        }
        if (existing != codes) {
            tabs.innerHTML = ""
            leagues.forEach { league ->
                val btn = button(league.title, "league-tab") {
                    if (AppState.selectedLeague == league.code) return@button
                    AppState.selectedLeague = league.code
                    AppState.selectedRound = null
                    clearFlash()
                    syncLeagueTabActive()
                    paintBody()
                }
                btn.setAttribute("data-league", league.code)
                btn.title = league.subtitle
                tabs.appendChild(btn)
            }
        }
        syncLeagueTabActive()
    }

    fun paint() {
        ensureLeagueTabs()
        paintBody()
    }

    refreshNextMatchWidget = { paintNextMatch() }

    if (AppState.lineDataReady) {
        paint()
    } else {
        list.appendChild(el("p", "muted", "Загружаем линию…"))
        scope.launch {
            runCatching {
                preloadLineData()
                paint()
            }.onFailure {
                list.innerHTML = ""
                list.appendChild(el("p", "banner error", errorMessage(it)))
            }
        }
    }
    return section
}

private fun pollCard(poll: PollDto): HTMLElement {
    val card = el("article", "poll")
    card.setAttribute("data-poll-id", poll.id.toString())
    val shownPrediction = predictionForPoll(poll)
    val confirmedPrediction = poll.myPrediction ?: AppState.confirmedPredictions[poll.id]
    val won = poll.status == PollStatus.SETTLED &&
        shownPrediction != null &&
        poll.result != null &&
        shownPrediction == poll.result
    if (won) {
        card.appendChild(el("span", "poll-win-ribbon", "WIN"))
    }

    val title = el("div", "poll-title")
    val matchup = el("div", "matchup")
    matchup.appendChild(teamSide(poll.homeTeam, poll.homeLogoUrl))
    if (poll.homeScore != null && poll.awayScore != null) {
        val scoreBlock = el("div", "matchup-score")
        scoreBlock.appendChild(el("span", "matchup-score-main", "${poll.homeScore}:${poll.awayScore}"))
        if (poll.homePenScore != null && poll.awayPenScore != null) {
            scoreBlock.appendChild(
                el("span", "matchup-score-pen", "пен. ${poll.homePenScore}:${poll.awayPenScore}")
            )
        }
        matchup.appendChild(scoreBlock)
    } else {
        matchup.appendChild(el("span", "matchup-vs", "—"))
    }
    matchup.appendChild(teamSide(poll.awayTeam, poll.awayLogoUrl))
    title.appendChild(matchup)
    title.appendChild(el("span", "badge ${poll.status.name.lowercase()}", statusLabel(poll.status)))
    card.appendChild(title)

    card.appendChild(
        el(
            "p",
            "meta",
            "${formatRoundLabel(poll.roundName)} · Старт: ${formatDateTime(poll.kickoffAt)} · ${sourceLabel(poll.source)}"
        )
    )

    if (poll.myPrediction != null) {
        card.appendChild(votesView(poll))
    } else if (confirmedPrediction != null) {
        card.appendChild(
            el(
                "p",
                "prediction-saved",
                "Ваш прогноз сохранён: ${outcomeChoiceLabel(poll, confirmedPrediction)}. Обновляем голоса…"
            )
        )
    } else if (AppState.user != null && poll.status == PollStatus.OPEN) {
        card.appendChild(el("p", "votes-locked", "Голоса откроются после вашего прогноза"))
    }

    if (poll.result != null && (poll.homeScore == null || poll.awayScore == null)) {
        card.appendChild(el("p", "result", formatMatchResultLine(poll)))
    }

    if (
        poll.status == PollStatus.OPEN &&
        AppState.user != null &&
        poll.myPrediction == null &&
        confirmedPrediction == null
    ) {
        val row = el("div", "actions prediction-actions")
        val idleTimerText = "После выбора у вас будет 10 секунд на изменение решения."
        val timer = el("p", "prediction-timer", idleTimerText)
        val voteError = el("p", "form-error")
        voteError.setAttribute("hidden", "")
        val outcomeButtons = mutableListOf<Triple<MatchOutcome, HTMLElement, String>>()
        val userId = AppState.user!!.id
        val restoredPending = PendingPredictions.get(userId, poll.id)
        var selected: MatchOutcome? = restoredPending?.choice
        var secondsLeft = restoredPending?.let(PendingPredictions::remainingSeconds) ?: 10
        var intervalId: Int? = null
        var submitting = PendingPredictions.isSubmitting(userId, poll.id)
        var pressedOutcome: MatchOutcome? = null
        var releaseFadeTimeout: Int? = null

        fun paintButton(btn: HTMLElement, label: String, cancelMode: Boolean, animate: Boolean) {
            val wantText = if (cancelMode) "Отменить" else label
            val alreadyCancel = btn.classList.contains("is-cancel")
            if (alreadyCancel == cancelMode && btn.textContent == wantText && !btn.classList.contains("is-flipping")) {
                return
            }

            fun applyState() {
                btn.classList.remove("is-pressed", "ghost", "primary", "is-cancel")
                btn.textContent = wantText
                if (cancelMode) {
                    btn.classList.add("primary", "prediction-choice", "is-cancel")
                    btn.setAttribute("aria-label", "Отменить выбор")
                } else {
                    btn.classList.add("ghost", "prediction-choice")
                    btn.removeAttribute("aria-label")
                }
            }

            if (!animate) {
                btn.classList.remove("is-flipping")
                applyState()
                return
            }

            btn.classList.remove("is-flipping")
            btn.asDynamic().offsetWidth
            btn.classList.add("is-flipping")
            window.setTimeout({
                btn.classList.remove("is-pressed", "ghost", "primary", "is-cancel")
                btn.textContent = wantText
                if (cancelMode) {
                    btn.classList.add("primary", "prediction-choice", "is-cancel", "is-flipping")
                    btn.setAttribute("aria-label", "Отменить выбор")
                } else {
                    btn.classList.add("ghost", "prediction-choice", "is-flipping")
                    btn.removeAttribute("aria-label")
                }
            }, 240)
            window.setTimeout({
                btn.classList.remove("is-flipping")
            }, 520)
        }

        fun restoreAllLabels(animate: Boolean) {
            outcomeButtons.forEach { (_, btn, label) ->
                paintButton(btn, label, cancelMode = false, animate = animate)
                btn.removeAttribute("disabled")
            }
        }

        fun refreshPendingChoice(animate: Boolean) {
            outcomeButtons.forEach { (outcome, btn, label) ->
                paintButton(btn, label, cancelMode = selected == outcome, animate = animate)
            }
            timer.textContent =
                "Отправка через $secondsLeft сек. Нажмите «Отменить» на выбранном исходе или смените выбор."
            voteError.setAttribute("hidden", "")
        }

        fun cancelPending() {
            if (submitting) return
            releaseFadeTimeout?.let { window.clearTimeout(it) }
            releaseFadeTimeout = null
            PendingPredictions.cancel(userId, poll.id)
            PollCardCountdownViews.clear(poll.id)
            intervalId = null
            selected = null
            secondsLeft = 10
            restoreAllLabels(animate = true)
            voteError.setAttribute("hidden", "")
            voteError.textContent = ""
            timer.textContent = idleTimerText
        }

        fun ensureTimerRunning() {
            if (intervalId != null) return
            val id = window.setInterval({
                val pending = PendingPredictions.get(userId, poll.id)
                submitting = PendingPredictions.isSubmitting(userId, poll.id)
                secondsLeft = pending?.let(PendingPredictions::remainingSeconds) ?: 0
                if (submitting || secondsLeft <= 0) {
                    outcomeButtons.forEach { (_, button, _) -> button.setAttribute("disabled", "true") }
                    timer.textContent = "Сохраняем прогноз…"
                } else {
                    timer.textContent =
                        "Отправка через $secondsLeft сек. Нажмите «Отменить» на выбранном исходе или смените выбор."
                }
            }, 1_000)
            intervalId = id
            PollCardCountdownViews.set(poll.id, id)
        }

        fun selectOutcome(outcome: MatchOutcome) {
            val pending = PendingPredictions.select(userId, poll.id, outcome)
            selected = pending.choice
            secondsLeft = PendingPredictions.remainingSeconds(pending)
            refreshPendingChoice(animate = true)
            ensureTimerRunning()
            timer.textContent =
                "Отправка через $secondsLeft сек. Нажмите «Отменить» на выбранном исходе или смените выбор."
        }

        listOf(MatchOutcome.HOME_WIN, MatchOutcome.DRAW, MatchOutcome.AWAY_WIN).forEach { outcome ->
            val label = outcomeChoiceLabel(poll, outcome)
            val btn = el("button", "ghost prediction-choice", label)
            btn.addEventListener("pointerdown", { ev ->
                if (submitting) return@addEventListener
                val pe = ev.asDynamic()
                if (pe.button != null && pe.button != 0) return@addEventListener
                pressedOutcome = outcome
                btn.classList.add("is-pressed")
                runCatching { btn.asDynamic().setPointerCapture(pe.pointerId) }
            })
            btn.addEventListener("pointerup", {
                if (submitting) return@addEventListener
                btn.classList.remove("is-pressed")
                if (pressedOutcome != outcome) return@addEventListener
                pressedOutcome = null
                releaseFadeTimeout?.let { window.clearTimeout(it) }
                if (selected == outcome) {
                    releaseFadeTimeout = null
                    cancelPending()
                } else {
                    // Let green press state ease out, then flip to cancel.
                    releaseFadeTimeout = window.setTimeout({
                        releaseFadeTimeout = null
                        if (!submitting) selectOutcome(outcome)
                    }, 300)
                }
            })
            btn.addEventListener("pointercancel", {
                btn.classList.remove("is-pressed")
                if (pressedOutcome == outcome) pressedOutcome = null
                releaseFadeTimeout?.let { window.clearTimeout(it) }
                releaseFadeTimeout = null
            })
            btn.addEventListener("pointerleave", {
                if (pressedOutcome == outcome && !btn.classList.contains("is-flipping")) {
                    // keep pressed if capture held; otherwise clear visual
                    if (pressedOutcome == outcome) {
                        // no-op while capturing; release via pointerup
                    }
                }
            })
            // Avoid duplicate activation from click after pointerup
            btn.addEventListener("click", { ev ->
                ev.preventDefault()
            })
            outcomeButtons += Triple(outcome, btn, label)
            row.appendChild(btn)
        }
        if (selected != null) {
            refreshPendingChoice(animate = false)
            ensureTimerRunning()
        }
        card.appendChild(row)
        card.appendChild(timer)
        card.appendChild(voteError)
    }

    if (AppState.user?.role == UserRole.ADMIN && poll.status != PollStatus.SETTLED) {
        val settle = el("div", "actions settle")
        settle.appendChild(el("span", "muted", "Зафиксировать итог:"))
        listOf(MatchOutcome.HOME_WIN, MatchOutcome.DRAW, MatchOutcome.AWAY_WIN).forEach { outcome ->
            settle.appendChild(button(outcomeChoiceLabel(poll, outcome), "ghost") {
                scope.launch {
                    runCatching {
                        ApiClient.settlePoll(poll.id, outcome)
                        AppState.notice = "Опрос рассчитан"
                        invalidateLineCaches()
                        preloadLineData()
                        render()
                    }.onFailure {
                        AppState.error = errorMessage(it)
                        render()
                    }
                }
            })
        }
        card.appendChild(settle)
    }
    return card
}

private fun ratingPage(): HTMLElement {
    val section = el("section", "page rating-page")
    section.appendChild(el("h1", text = "Рейтинг сезона"))
    section.appendChild(
        el(
            "p",
            "lede",
            "Топ-10 сезона или выбранного тура. Если вы вне десятки — ниже показана ваша позиция с соседями."
        )
    )

    val toolbar = el("div", "rating-toolbar no-print")
    val tabs = el("div", "league-tabs")
    val actions = el("div", "rating-toolbar-actions")
    val roundSelect = document.createElement("select") as HTMLSelectElement
    roundSelect.className = "round-filter-select"
    roundSelect.setAttribute("aria-label", "Выбор тура")
    val pdfButton = button("Сохранить PDF", "ghost") {}
    actions.appendChild(roundSelect)
    actions.appendChild(pdfButton)
    toolbar.appendChild(tabs)
    toolbar.appendChild(actions)
    section.appendChild(toolbar)

    val printArea = el("div", "rating-print-area")
    section.appendChild(printArea)

    pdfButton.addEventListener("click", {
        window.print()
    })

    fun syncLeagueTabActive() {
        val selected = AppState.selectedRatingLeague.orEmpty()
        val nodes = tabs.querySelectorAll("button.league-tab")
        for (i in 0 until nodes.length) {
            val btn = nodes.item(i) as? HTMLElement ?: continue
            if (btn.getAttribute("data-league").orEmpty() == selected) {
                btn.classList.add("active")
            } else {
                btn.classList.remove("active")
            }
        }
        scrollLeagueTabIntoView(tabs, selected)
        bindLeagueTabsEdgeFade(tabs)
    }

    fun availableRatingRounds(): List<Int> {
        val league = AppState.selectedRatingLeague ?: return emptyList()
        return AppState.pollsCache[league].orEmpty()
            .mapNotNull { roundNumber(it.roundName) }
            .distinct()
            .sorted()
    }

    fun syncRoundSelect() {
        val league = AppState.selectedRatingLeague
        if (league == null) {
            roundSelect.hidden = true
            roundSelect.innerHTML = ""
            return
        }
        roundSelect.hidden = false
        val rounds = availableRatingRounds()
        if (AppState.selectedRatingRound != null && AppState.selectedRatingRound !in rounds) {
            AppState.selectedRatingRound = null
        }
        roundSelect.innerHTML = "<option value=\"\">Сезон</option>" +
            rounds.joinToString("") { round ->
                "<option value=\"$round\">Тур $round</option>"
            }
        roundSelect.value = AppState.selectedRatingRound?.toString().orEmpty()
    }

    fun renderBoard(rows: List<RatingEntryDto>) {
        val viewerId = AppState.user?.id
        val tourView = AppState.selectedRatingLeague != null && AppState.selectedRatingRound != null
        val top = rows.take(RATING_TOP_SIZE)
        val aroundMe = aroundMeRatings(rows, viewerId)
        val boardTitle = ratingBoardTitle()

        printArea.innerHTML = ""
        val printHeader = el("div", "rating-print-header rating-print-only")
        printHeader.appendChild(el("strong", text = "Футбольные гуси"))
        printHeader.appendChild(el("span", text = boardTitle))
        printHeader.appendChild(el("span", "muted", "Сформировано: ${formatPrintDateTime()}"))
        printArea.appendChild(printHeader)
        printArea.appendChild(el("h2", "rating-board-title no-print", boardTitle))
        if (tourView) {
            printArea.appendChild(
                el(
                    "p",
                    "muted rating-tour-hint",
                    "Учтены только рассчитанные матчи тура. Первое место — победитель тура."
                )
            )
        }

        if (rows.isEmpty()) {
            printArea.appendChild(el("p", "empty", "Пока нет рассчитанных прогнозов"))
        } else {
            printArea.appendChild(el("h3", "rating-section-title", "Топ $RATING_TOP_SIZE"))
            printArea.appendChild(buildRatingTable(top, viewerId, markTourWinner = tourView))
            if (aroundMe.isNotEmpty()) {
                val around = el("section", "rating-around")
                around.appendChild(el("h3", "rating-section-title", "Ваша позиция"))
                around.appendChild(
                    el(
                        "p",
                        "muted rating-around-hint",
                        "Места рядом с вами в текущем рейтинге."
                    )
                )
                around.appendChild(buildRatingTable(aroundMe, viewerId, markTourWinner = false))
                printArea.appendChild(around)
            }
        }
    }

    fun paintBoard() {
        syncRoundSelect()
        val league = AppState.selectedRatingLeague
        val round = if (league == null) null else AppState.selectedRatingRound
        val key = ratingCacheKey(league, round)
        val cached = AppState.ratingsCache[key]
        if (cached != null) {
            renderBoard(cached)
            return
        }
        printArea.innerHTML = ""
        printArea.appendChild(el("p", "muted", "Загружаем рейтинг…"))
        val leagueAtStart = league
        val roundAtStart = round
        scope.launch {
            runCatching {
                ApiClient.ratings(league = leagueAtStart, round = roundAtStart)
            }.onSuccess { rows ->
                AppState.ratingsCache = AppState.ratingsCache + (key to rows)
                if (AppState.page != Page.RATING) return@onSuccess
                if (AppState.selectedRatingLeague != leagueAtStart) return@onSuccess
                val currentRound = if (AppState.selectedRatingLeague == null) null else AppState.selectedRatingRound
                if (currentRound != roundAtStart) return@onSuccess
                renderBoard(rows)
            }.onFailure {
                if (AppState.page != Page.RATING) return@onFailure
                printArea.innerHTML = ""
                printArea.appendChild(el("p", "banner error", errorMessage(it)))
            }
        }
    }

    fun ensureLeagueTabs() {
        val leagues = AppState.leagues
        val codes = listOf("") + leagues.map { it.code }
        val existing = mutableListOf<String>()
        val nodes = tabs.querySelectorAll("button.league-tab")
        for (i in 0 until nodes.length) {
            existing.add((nodes.item(i) as? HTMLElement)?.getAttribute("data-league").orEmpty())
        }
        if (existing != codes) {
            tabs.innerHTML = ""
            tabs.appendChild(
                button("Общий", "league-tab") {
                    if (AppState.selectedRatingLeague == null) return@button
                    AppState.selectedRatingLeague = null
                    AppState.selectedRatingRound = null
                    clearFlash()
                    syncLeagueTabActive()
                    paintBoard()
                }.also { it.setAttribute("data-league", "") }
            )
            leagues.forEach { league ->
                tabs.appendChild(
                    button(league.title, "league-tab") {
                        if (AppState.selectedRatingLeague == league.code) return@button
                        AppState.selectedRatingLeague = league.code
                        AppState.selectedRatingRound = null
                        clearFlash()
                        syncLeagueTabActive()
                        paintBoard()
                    }.also { it.setAttribute("data-league", league.code) }
                )
            }
        }
        syncLeagueTabActive()
    }

    roundSelect.addEventListener("change", {
        AppState.selectedRatingRound = roundSelect.value.toIntOrNull()
        clearFlash()
        paintBoard()
    })

    fun paint() {
        ensureLeagueTabs()
        paintBoard()
    }

    if (AppState.lineDataReady) {
        paint()
    } else {
        printArea.appendChild(el("p", "muted", "Загружаем рейтинг…"))
        scope.launch {
            runCatching {
                preloadLineData()
                paint()
            }.onFailure {
                printArea.innerHTML = ""
                printArea.appendChild(el("p", "banner error", errorMessage(it)))
            }
        }
    }
    return section
}

private const val RATING_TOP_SIZE = 10

private fun ratingCacheKey(league: String?, round: Int?): String {
    if (league == null) return "overall"
    if (round == null) return league
    return "$league|$round"
}

private fun ratingBoardTitle(): String {
    val code = AppState.selectedRatingLeague
    if (code == null) return "Общий рейтинг"
    val league = AppState.leagues.firstOrNull { it.code == code }
    val leagueTitle = league?.title ?: code
    val round = AppState.selectedRatingRound
    return if (round == null) leagueTitle else "$leagueTitle · Тур $round"
}

private fun aroundMeRatings(rows: List<RatingEntryDto>, viewerId: Long?): List<RatingEntryDto> {
    if (viewerId == null || rows.isEmpty()) return emptyList()
    val meIndex = rows.indexOfFirst { it.userId == viewerId }
    if (meIndex < 0) return emptyList()
    if (rows[meIndex].rank <= RATING_TOP_SIZE) return emptyList()
    val from = (meIndex - 1).coerceAtLeast(0)
    val to = (meIndex + 1).coerceAtMost(rows.lastIndex)
    return rows.subList(from, to + 1)
}

private fun buildRatingTable(
    rows: List<RatingEntryDto>,
    viewerId: Long?,
    markTourWinner: Boolean
): HTMLElement {
    val table = el("table", "rating")
    table.innerHTML =
        "<thead><tr><th>#</th><th>Игрок</th><th>Угадано</th><th>Всего</th><th>%</th></tr></thead>"
    val tbody = el("tbody")
    rows.forEach { r ->
        val isMe = viewerId != null && r.userId == viewerId
        val isWinner = markTourWinner && r.rank == 1
        val rowClass = when {
            isMe && isWinner -> "rating-me rating-winner"
            isMe -> "rating-me"
            isWinner -> "rating-winner"
            else -> null
        }
        val tr = el("tr", rowClass)
        tr.appendChild(el("td", text = r.rank.toString()))
        tr.appendChild(
            el("td").also { cell ->
                cell.appendChild(userBadge(r.displayName, r.avatarIcon))
                if (isWinner) {
                    cell.appendChild(el("span", "rating-winner-badge", "Победитель тура"))
                }
            }
        )
        tr.appendChild(el("td", text = r.correctPredictions.toString()))
        tr.appendChild(el("td", text = r.totalPredictions.toString()))
        tr.appendChild(el("td", text = "${r.accuracyPercent}%"))
        tbody.appendChild(tr)
    }
    table.appendChild(tbody)
    return table
}

private fun formatPrintDateTime(): String {
    val iso = js("new Date().toISOString()") as String
    return formatDateTime(iso)
}

private fun achievementsPage(): HTMLElement {
    val section = el("section", "page")
    section.appendChild(el("h1", text = "Ачивки"))
    section.appendChild(el("p", "lede", "Открытые награды можно поставить аватаром у имени."))
    val grid = el("div", "ach-grid")
    section.appendChild(grid)

    scope.launch {
        runCatching {
            val items = ApiClient.achievements()
            grid.innerHTML = ""
            items.forEach { a ->
                val card = el("article", if (a.unlocked) "ach unlocked" else "ach locked")
                val img = document.createElement("img") as HTMLImageElement
                img.className = "ach-image"
                img.src = achievementImageUrl(a.icon)
                img.alt = a.title
                card.appendChild(img)
                card.appendChild(el("h3", text = a.title))
                card.appendChild(el("p", "ach-desc", a.description))
                if (a.unlocked) {
                    card.appendChild(el("span", "ach-status", formatUnlockDate(a.unlockedAt)))
                    val current = AppState.user?.avatarIcon?.equals(a.icon, ignoreCase = true) == true
                    if (current) {
                        card.appendChild(el("span", "ach-avatar-current", "Текущий аватар"))
                    } else {
                        card.appendChild(button("Использовать как аватар", "ghost") {
                            scope.launch {
                                runCatching {
                                    AppState.user = ApiClient.setAvatar(a.code)
                                    AppState.notice = "Аватар обновлён"
                                    AppState.error = null
                                    render()
                                }.onFailure {
                                    AppState.error = errorMessage(it)
                                    render()
                                }
                            }
                        })
                    }
                } else {
                    card.appendChild(el("span", "ach-status", "Ещё не открыто"))
                }
                grid.appendChild(card)
            }
            setViewportEdgeFadeEnabled(true)
        }.onFailure {
            grid.innerHTML = ""
            grid.appendChild(el("p", "banner error", errorMessage(it)))
        }
    }
    return section
}

private fun adminInactivityReminderBlock(): HTMLElement {
    val box = el("div", "admin-block")
    box.appendChild(el("h2", text = "Напоминание неактивным"))
    box.appendChild(
        el(
            "p",
            "muted",
            "Если пользователь не заходил в приложение N дней — inbox + пуш «Мы по тебе скучаем». " +
                "Повтор только после нового визита и снова N дней тишины."
        )
    )
    val status = el("p", "muted", "Загрузка…")
    box.appendChild(status)
    val actions = el("div", "actions")
    box.appendChild(actions)
    val daysInput = input("number", "5")
    daysInput.setAttribute("min", "1")
    daysInput.setAttribute("max", "365")
    daysInput.style.maxWidth = "8rem"
    box.appendChild(field("Дней без входа", daysInput))
    box.appendChild(button("Сохранить срок", "primary") {
        val days = daysInput.value.toIntOrNull()
        if (days == null || days !in 1..365) {
            AppState.error = "Укажите срок от 1 до 365 дней"
            render()
            return@button
        }
        scope.launch {
            runCatching {
                ApiClient.updateAdminNotificationSettings(
                    UpdateNotificationSettingsRequest(inactivityReminderDays = days)
                )
                AppState.notice = "Срок напоминания: $days дн."
                AppState.error = null
                render()
            }.onFailure {
                AppState.error = errorMessage(it)
                render()
            }
        }
    })

    scope.launch {
        runCatching {
            val settings = ApiClient.adminNotificationSettings()
            status.textContent =
                "Сейчас: ${onOff(settings.inactivityReminderEnabled)}, срок ${settings.inactivityReminderDays} дн."
            daysInput.value = settings.inactivityReminderDays.toString()
            actions.innerHTML = ""
            actions.appendChild(
                button(
                    if (settings.inactivityReminderEnabled) {
                        "Выключить напоминания"
                    } else {
                        "Включить напоминания"
                    },
                    "ghost"
                ) {
                    scope.launch {
                        runCatching {
                            ApiClient.updateAdminNotificationSettings(
                                UpdateNotificationSettingsRequest(
                                    inactivityReminderEnabled = !settings.inactivityReminderEnabled
                                )
                            )
                            AppState.notice = "Настройки напоминаний обновлены"
                            render()
                        }.onFailure {
                            AppState.error = errorMessage(it)
                            render()
                        }
                    }
                }
            )
        }.onFailure {
            status.className = "form-error"
            status.textContent = errorMessage(it)
        }
    }
    return box
}

private fun adminNotificationsBlock(): HTMLElement {
    val box = el("div", "admin-block")
    box.appendChild(el("h2", text = "Уведомления и рассылки"))
    box.appendChild(
        el(
            "p",
            "muted",
            "Kill-switch пушей и master-кампании. Inbox пользователям пишется всегда; пуши можно отключить."
        )
    )

    val settingsStatus = el("p", "muted", "Загрузка настроек…")
    box.appendChild(settingsStatus)
    val toggles = el("div", "actions")
    box.appendChild(toggles)

    val titleInput = input("text", "Заголовок рассылки")
    val bodyInput = input("text", "Текст рассылки")
    box.appendChild(field("Заголовок", titleInput))
    box.appendChild(field("Текст", bodyInput))
    box.appendChild(button("Создать и запустить рассылку всем", "primary") {
        val title = titleInput.value.trim()
        val body = bodyInput.value.trim()
        if (title.isEmpty() || body.isEmpty()) {
            AppState.error = "Укажите заголовок и текст"
            render()
            return@button
        }
        if (!window.confirm("Отправить рассылку всем пользователям?")) return@button
        scope.launch {
            runCatching {
                val campaign = ApiClient.createNotificationCampaign(
                    CreateNotificationCampaignRequest(title = title, body = body)
                )
                ApiClient.startNotificationCampaign(campaign.id)
                AppState.notice = "Кампания #${campaign.id} запущена"
                AppState.error = null
                render()
            }.onFailure {
                AppState.error = errorMessage(it)
                render()
            }
        }
    })

    box.appendChild(el("h3", text = "Пуш одному пользователю"))
    box.appendChild(
        el(
            "p",
            "muted",
            "Укажите отображаемое имя (не логин). Система найдёт связку имя → логин → id и отправит inbox + пуш."
        )
    )
    val nameInput = input("text", "Например: Гусь")
    nameInput.setAttribute("list", "admin-notif-display-names")
    nameInput.setAttribute("autocomplete", "off")
    val nameDatalist = document.createElement("datalist")
    nameDatalist.id = "admin-notif-display-names"
    box.appendChild(field("Имя пользователя", nameInput))
    box.appendChild(nameDatalist)
    val resolveStatus = el("p", "muted", "Пользователь ещё не выбран.")
    box.appendChild(resolveStatus)
    var resolvedUser: UserDto? = null
    var adminUsersCache: List<UserDto> = emptyList()

    fun applyResolved(user: UserDto?) {
        resolvedUser = user
        if (user == null) {
            resolveStatus.className = "muted"
            resolveStatus.textContent = "Пользователь ещё не выбран."
        } else {
            resolveStatus.className = "muted"
            resolveStatus.textContent =
                "Найден: ${user.displayName} · логин ${user.username} · id ${user.id}"
        }
    }

    fun resolveByDisplayName(raw: String): UserDto? {
        val needle = raw.trim()
        if (needle.isEmpty()) {
            applyResolved(null)
            resolveStatus.textContent = "Введите отображаемое имя."
            return null
        }
        val exact = adminUsersCache.filter { it.displayName.equals(needle, ignoreCase = true) }
        val matches = when {
            exact.size == 1 -> exact
            exact.size > 1 -> exact
            else -> adminUsersCache.filter { it.displayName.contains(needle, ignoreCase = true) }
        }
        return when {
            matches.isEmpty() -> {
                applyResolved(null)
                resolveStatus.className = "form-error"
                resolveStatus.textContent = "Пользователь с именем «$needle» не найден."
                null
            }
            matches.size > 1 -> {
                applyResolved(null)
                resolveStatus.className = "form-error"
                resolveStatus.textContent =
                    "Несколько совпадений: " +
                        matches.joinToString("; ") { "${it.displayName} (${it.username}, id ${it.id})" } +
                        ". Уточните имя."
                null
            }
            else -> {
                applyResolved(matches.first())
                matches.first()
            }
        }
    }

    val singleActions = el("div", "actions")
    singleActions.appendChild(button("Найти", "ghost") {
        resolveByDisplayName(nameInput.value)
    })
    singleActions.appendChild(button("Отправить этому пользователю", "primary") {
        val title = titleInput.value.trim()
        val body = bodyInput.value.trim()
        if (title.isEmpty() || body.isEmpty()) {
            AppState.error = "Укажите заголовок и текст"
            render()
            return@button
        }
        val user = resolvedUser ?: resolveByDisplayName(nameInput.value) ?: return@button
        if (!window.confirm(
                "Отправить «$title» пользователю ${user.displayName} (${user.username}, id ${user.id})?"
            )
        ) {
            return@button
        }
        scope.launch {
            runCatching {
                val campaign = ApiClient.createNotificationCampaign(
                    CreateNotificationCampaignRequest(
                        title = title,
                        body = body,
                        audience = NotificationCampaignAudience.USER_IDS,
                        userIds = listOf(user.id)
                    )
                )
                ApiClient.startNotificationCampaign(campaign.id)
                AppState.notice =
                    "Кампания #${campaign.id} отправлена ${user.displayName} (${user.username})"
                AppState.error = null
                render()
            }.onFailure {
                AppState.error = errorMessage(it)
                render()
            }
        }
    })
    box.appendChild(singleActions)
    nameInput.addEventListener("change", {
        resolveByDisplayName(nameInput.value)
    })
    nameInput.addEventListener("keydown", { ev ->
        val ke = ev.asDynamic()
        if (ke.key == "Enter") {
            ev.preventDefault()
            resolveByDisplayName(nameInput.value)
        }
    })

    val campaignsList = el("div", "notif-campaigns")
    campaignsList.appendChild(el("p", "muted", "Кампании…"))
    box.appendChild(campaignsList)

    scope.launch {
        runCatching {
            adminUsersCache = ApiClient.adminUsers()
            nameDatalist.innerHTML = ""
            adminUsersCache
                .map { it.displayName.trim() }
                .filter { it.isNotEmpty() }
                .distinctBy { it.lowercase() }
                .sortedBy { it.lowercase() }
                .forEach { name ->
                    val opt = document.createElement("option")
                    opt.setAttribute("value", name)
                    nameDatalist.appendChild(opt)
                }
        }.onFailure {
            resolveStatus.className = "form-error"
            resolveStatus.textContent = "Не удалось загрузить пользователей: ${errorMessage(it)}"
        }
    }

    scope.launch {
        runCatching {
            val settings = ApiClient.adminNotificationSettings()
            settingsStatus.textContent =
                "Пуши: ${onOff(settings.pushesEnabled)}; " +
                    "угадал: ${onOff(settings.predictionCorrectPushEnabled)}; " +
                    "админ: ${onOff(settings.adminBroadcastsEnabled)}; " +
                    "стикерпаки: ${onOff(settings.stickerPackPushEnabled)}; " +
                    "неактивным: ${onOff(settings.inactivityReminderEnabled)} (${settings.inactivityReminderDays} дн.)"
            toggles.innerHTML = ""
            toggles.appendChild(button(if (settings.pushesEnabled) "Выключить все пуши" else "Включить пуши", "ghost") {
                scope.launch {
                    runCatching {
                        ApiClient.updateAdminNotificationSettings(
                            UpdateNotificationSettingsRequest(pushesEnabled = !settings.pushesEnabled)
                        )
                        AppState.notice = "Настройки уведомлений обновлены"
                        render()
                    }.onFailure {
                        AppState.error = errorMessage(it)
                        render()
                    }
                }
            })
            toggles.appendChild(
                button(
                    if (settings.adminBroadcastsEnabled) "Выключить админ-рассылки" else "Включить админ-рассылки",
                    "ghost"
                ) {
                    scope.launch {
                        runCatching {
                            ApiClient.updateAdminNotificationSettings(
                                UpdateNotificationSettingsRequest(
                                    adminBroadcastsEnabled = !settings.adminBroadcastsEnabled
                                )
                            )
                            AppState.notice = "Настройки уведомлений обновлены"
                            render()
                        }.onFailure {
                            AppState.error = errorMessage(it)
                            render()
                        }
                    }
                }
            )
            toggles.appendChild(
                button(
                    if (settings.stickerPackPushEnabled) "Выключить пуши стикерпаков" else "Включить пуши стикерпаков",
                    "ghost"
                ) {
                    scope.launch {
                        runCatching {
                            ApiClient.updateAdminNotificationSettings(
                                UpdateNotificationSettingsRequest(
                                    stickerPackPushEnabled = !settings.stickerPackPushEnabled
                                )
                            )
                            AppState.notice = "Настройки уведомлений обновлены"
                            render()
                        }.onFailure {
                            AppState.error = errorMessage(it)
                            render()
                        }
                    }
                }
            )

            val campaigns = ApiClient.adminNotificationCampaigns()
            campaignsList.innerHTML = ""
            if (campaigns.isEmpty()) {
                campaignsList.appendChild(el("p", "muted", "Кампаний пока нет."))
            } else {
                campaigns.take(10).forEach { c ->
                    val row = el("div", "notif-campaign-row")
                    row.appendChild(
                        el(
                            "p",
                            text = "#${c.id} [${c.status}] ${c.title} — targeted ${c.targeted}, sent ${c.sent}, failed ${c.failed}"
                        )
                    )
                    val actions = el("div", "actions")
                    if (c.status == NotificationCampaignStatus.DRAFT ||
                        c.status == NotificationCampaignStatus.SCHEDULED ||
                        c.status == NotificationCampaignStatus.PAUSED
                    ) {
                        actions.appendChild(button("Старт", "ghost") {
                            scope.launch {
                                runCatching {
                                    ApiClient.startNotificationCampaign(c.id)
                                    AppState.notice = "Кампания #${c.id} запущена"
                                    render()
                                }.onFailure {
                                    AppState.error = errorMessage(it)
                                    render()
                                }
                            }
                        })
                    }
                    if (c.status == NotificationCampaignStatus.RUNNING ||
                        c.status == NotificationCampaignStatus.SCHEDULED
                    ) {
                        actions.appendChild(button("Пауза", "ghost") {
                            scope.launch {
                                runCatching {
                                    ApiClient.pauseNotificationCampaign(c.id)
                                    render()
                                }.onFailure {
                                    AppState.error = errorMessage(it)
                                    render()
                                }
                            }
                        })
                    }
                    if (c.status != NotificationCampaignStatus.COMPLETED &&
                        c.status != NotificationCampaignStatus.CANCELLED
                    ) {
                        actions.appendChild(button("Отмена", "ghost") {
                            scope.launch {
                                runCatching {
                                    ApiClient.cancelNotificationCampaign(c.id)
                                    render()
                                }.onFailure {
                                    AppState.error = errorMessage(it)
                                    render()
                                }
                            }
                        })
                    }
                    row.appendChild(actions)
                    campaignsList.appendChild(row)
                }
            }
        }.onFailure {
            settingsStatus.textContent = errorMessage(it)
            campaignsList.innerHTML = ""
            campaignsList.appendChild(el("p", "form-error", errorMessage(it)))
        }
    }
    return box
}

private fun onOff(value: Boolean): String = if (value) "вкл" else "выкл"

private fun adminPage(): HTMLElement {
    val section = el("section", "page")
    section.appendChild(el("h1", text = "Админ"))
    section.appendChild(el("p", "lede", "Синхронизация реальных матчей и ручное создание."))

    val passwordBox = el("div", "admin-block")
    passwordBox.appendChild(el("h2", text = "Сброс пароля пользователя"))
    passwordBox.appendChild(
        el(
            "p",
            "muted",
            "Будет создан временный пароль, а активные сессии пользователя завершатся. " +
                "После входа пользователь должен установить новый пароль."
        )
    )
    val userSelect = document.createElement("select") as HTMLSelectElement
    userSelect.className = "admin-user-select"
    userSelect.setAttribute("aria-label", "Пользователь для сброса пароля")
    userSelect.innerHTML = "<option value=\"\">Загрузка пользователей…</option>"
    val passwordResult = el("div", "temporary-password-result")
    passwordBox.appendChild(userSelect)
    passwordBox.appendChild(button("Создать временный пароль", "ghost") {
        val userId = userSelect.value.toLongOrNull()
        if (userId == null) {
            passwordResult.textContent = "Выберите пользователя"
            return@button
        }
        val username = userSelect.options.item(userSelect.selectedIndex)?.textContent ?: "пользователя"
        if (!window.confirm("Сбросить пароль для $username? Все активные сессии будут завершены.")) {
            return@button
        }
        scope.launch {
            runCatching {
                val result = ApiClient.resetUserPassword(userId)
                passwordResult.innerHTML = ""
                passwordResult.appendChild(
                    el("p", "muted", "Передайте временный пароль пользователю ${result.user.username}. Он показан только сейчас:")
                )
                passwordResult.appendChild(el("code", "temporary-password", result.temporaryPassword))
            }.onFailure {
                passwordResult.innerHTML = ""
                passwordResult.appendChild(el("p", "form-error", errorMessage(it)))
            }
        }
    })
    passwordBox.appendChild(passwordResult)
    section.appendChild(passwordBox)

    section.appendChild(adminInactivityReminderBlock())

    scope.launch {
        runCatching {
            val users = ApiClient.adminUsers()
            userSelect.innerHTML = ""
            val placeholder = document.createElement("option")
            placeholder.setAttribute("value", "")
            placeholder.textContent = if (users.isEmpty()) "Нет пользователей" else "Выберите пользователя"
            userSelect.appendChild(placeholder)
            users.forEach { user ->
                val option = document.createElement("option")
                option.setAttribute("value", user.id.toString())
                option.textContent = "${user.displayName} (${user.username})" +
                    if (user.mustChangePassword) " — ожидает смены пароля" else ""
                userSelect.appendChild(option)
            }
        }.onFailure {
            userSelect.innerHTML = "<option value=\"\">Не удалось загрузить пользователей</option>"
            passwordResult.textContent = errorMessage(it)
        }
    }

    val feedBox = el("div", "admin-block")
    feedBox.appendChild(el("h2", text = "SStats.net"))
    val feedStatus = el("p", "muted", "Проверка статуса…")
    feedBox.appendChild(feedStatus)
    feedBox.appendChild(button("Синхронизировать матчи", "primary") {
        scope.launch {
            runCatching {
                val result = ApiClient.syncFeed()
                AppState.notice = result.message
                AppState.error = null
                AppState.page = Page.POLLS
                invalidateLineCaches()
                enterAppWithBootLoader()
            }.onFailure {
                AppState.error = errorMessage(it)
                render()
            }
        }
    })
    section.appendChild(feedBox)
    scope.launch {
        runCatching {
            feedStatus.textContent = ApiClient.feedStatus().message
        }.onFailure {
            feedStatus.textContent = errorMessage(it)
        }
    }

    if (NOTIFICATIONS_UI_ENABLED) {
        section.appendChild(adminNotificationsBlock())
    }

    section.appendChild(el("h2", text = "Ручной матч и опрос"))
    section.appendChild(
        el(
            "p",
            "muted",
            "Опрос создаётся автоматически при публикации матча. Кнопка «Создать опрос вручную» нужна только если матч уже есть, но опроса ещё нет."
        )
    )
    val home = input("text", "Хозяева")
    val away = input("text", "Гости")
    val kickoff = input("text", "Например, 2026-07-25T18:00:00")
    section.appendChild(field("Хозяева", home))
    section.appendChild(field("Гости", away))
    section.appendChild(field("Время (yyyy-MM-ddTHH:mm:ss)", kickoff))

    section.appendChild(button("Создать в РПЛ и опубликовать", "ghost") {
        scope.launch {
            runCatching {
                val season = ApiClient.activeSeason()
                val match = ApiClient.createMatch(
                    CreateMatchRequest(
                        seasonId = season.id,
                        league = League.RPL,
                        homeTeam = home.value.trim(),
                        awayTeam = away.value.trim(),
                        kickoffAt = kickoff.value.trim(),
                        publish = true
                    )
                )
                AppState.notice = "Матч #${match.id} опубликован в РПЛ — опрос создан автоматически"
                AppState.error = null
                AppState.selectedLeague = "RPL"
                AppState.page = Page.POLLS
                render()
            }.onFailure {
                AppState.error = errorMessage(it)
                render()
            }
        }
    })

    val matchId = input("number", "Match ID")
    val manualPollBox = el("div", "admin-block")
    manualPollBox.appendChild(el("h3", text = "Опрос для существующего матча"))
    manualPollBox.appendChild(field("ID матча", matchId))

    fun openPollsForMatch(id: Long, notice: String) {
        scope.launch {
            runCatching {
                val match = ApiClient.matches().find { it.id == id }
                    ?: throw IllegalStateException("Матч #$id не найден")
                AppState.selectedLeague = match.league.name
                AppState.notice = notice
                AppState.error = null
                AppState.page = Page.POLLS
                render()
            }.onFailure {
                AppState.error = errorMessage(it)
                AppState.notice = null
                render()
            }
        }
    }

    fun createPollForMatch(id: Long) {
        scope.launch {
            runCatching {
                val poll = ApiClient.createPoll(id)
                AppState.selectedLeague = poll.league.name
                AppState.notice = "Опрос #${poll.id}: ${poll.homeTeam} — ${poll.awayTeam}"
                AppState.error = null
                AppState.page = Page.POLLS
                render()
            }.onFailure {
                val msg = errorMessage(it)
                if (msg.contains("уже создан")) {
                    openPollsForMatch(id, "Опрос для матча #$id уже существует — открыта линия")
                    return@launch
                }
                AppState.error = msg
                AppState.notice = null
                render()
            }
        }
    }

    manualPollBox.appendChild(button("Создать опрос вручную", "ghost") {
        if (matchId.value.isBlank()) {
            AppState.error = "Укажите ID матча или выберите из списка ниже"
            AppState.notice = null
            render()
            return@button
        }
        val id = matchId.value.toLongOrNull()
        if (id == null) {
            AppState.error = "ID матча должен быть числом"
            AppState.notice = null
            render()
            return@button
        }
        createPollForMatch(id)
    })

    val matchesWithoutPoll = el("div", "admin-block")
    matchesWithoutPoll.appendChild(el("h3", text = "Матчи без опроса"))
    val matchesHint = el("p", "muted", "Загрузка…")
    val matchesActions = el("div", "actions")
    matchesWithoutPoll.appendChild(matchesHint)
    matchesWithoutPoll.appendChild(matchesActions)
    manualPollBox.appendChild(matchesWithoutPoll)
    section.appendChild(manualPollBox)

    scope.launch {
        runCatching {
            val all = ApiClient.matches()
            val withPoll = ApiClient.polls().map { it.matchId }.toSet()
            val withoutPoll = all.filter { it.status == MatchStatus.PUBLISHED && it.id !in withPoll }
            matchesActions.innerHTML = ""
            if (withoutPoll.isEmpty()) {
                matchesHint.textContent =
                    "У всех опубликованных матчей уже есть опросы. Чтобы добавить новый — создайте матч выше."
            } else {
                matchesHint.textContent = "Нажмите на матч, чтобы создать для него опрос:"
                withoutPoll.take(12).forEach { match ->
                    matchesActions.appendChild(
                        button("#${match.id} ${match.homeTeam} — ${match.awayTeam}", "ghost") {
                            createPollForMatch(match.id)
                        }
                    )
                }
            }
        }.onFailure {
            matchesHint.textContent = "Не удалось загрузить список матчей: ${errorMessage(it)}"
        }
    }
    return section
}

private fun statusLabel(status: PollStatus) = when (status) {
    PollStatus.OPEN -> "открыт"
    PollStatus.CLOSED -> "закрыт"
    PollStatus.SETTLED -> "рассчитан"
}

private fun outcomeChoiceLabel(poll: PollDto, o: MatchOutcome) = when (o) {
    MatchOutcome.HOME_WIN -> "победа ${poll.homeTeam}"
    MatchOutcome.DRAW -> "Ничья"
    MatchOutcome.AWAY_WIN -> "победа ${poll.awayTeam}"
}

private fun sourceLabel(source: PollSource) = when (source) {
    PollSource.AUTO -> "автоматический опрос"
    PollSource.MANUAL -> "ручной опрос"
}

private fun formatDateTime(value: String): String {
    val dateParts = value.substringBefore('T').split('-')
    val time = value.substringAfter('T', "").take(5)
    return if (dateParts.size == 3 && time.isNotBlank()) {
        "${dateParts[2]}.${dateParts[1]}.${dateParts[0]}, $time"
    } else {
        value.replace('T', ' ')
    }
}

private fun roundSortKey(roundName: String?): Int {
    return roundNumber(roundName) ?: Int.MAX_VALUE
}

private fun roundNumber(roundName: String?): Int? {
    if (roundName.isNullOrBlank()) return null
    return Regex("""(\d+)\s*$""").find(roundName.trim())?.groupValues?.get(1)?.toIntOrNull()
}

private fun formatRoundLabel(roundName: String?): String {
    if (roundName.isNullOrBlank()) return "Без тура"
    val num = Regex("""(\d+)\s*$""").find(roundName.trim())?.groupValues?.get(1)
    return if (num != null) "Тур $num" else roundName.trim()
}

/** Тур скрыт из «Все туры», если все матчи SETTLED и прошло ≥ 24ч после последнего kickoff. */
private fun isRoundArchived(roundPolls: List<PollDto>): Boolean {
    if (roundPolls.isEmpty()) return false
    if (roundPolls.any { it.status != PollStatus.SETTLED }) return false
    val lastKickoffMs = roundPolls.maxOf { kickoffMillis(it.kickoffAt) }
    val dayMs = 24L * 60L * 60L * 1000L
    return js("Date.now()") as Double >= (lastKickoffMs + dayMs).toDouble()
}

private fun kickoffMillis(value: String): Long {
    val iso = value.take(19)
    val parsed = js("Date.parse")("$iso+03:00") as Double
    return if (parsed.isNaN()) 0L else parsed.toLong()
}

private fun formatMatchResultLine(poll: PollDto): String {
    val outcome = poll.result ?: return "Итог: —"
    return when (outcome) {
        MatchOutcome.HOME_WIN -> "Итог: победа ${poll.homeTeam}"
        MatchOutcome.DRAW -> "Итог: ничья"
        MatchOutcome.AWAY_WIN -> "Итог: победа ${poll.awayTeam}"
    }
}

private fun teamSide(name: String, logoUrl: String?): HTMLElement {
    val side = el("div", "team-side")
    side.appendChild(teamLogo(name, logoUrl))
    side.appendChild(el("span", "team-name", name))
    return side
}

private fun teamLogo(name: String, logoUrl: String?): HTMLElement {
    val src = ApiClient.teamLogoSrc(logoUrl)
    if (!src.isNullOrBlank()) {
        val img = document.createElement("img") as HTMLImageElement
        img.className = "team-logo"
        img.src = src
        img.alt = name
        img.setAttribute("loading", "lazy")
        img.setAttribute("referrerpolicy", "no-referrer")
        img.addEventListener("error", {
            val fallback = el("span", "team-logo team-logo-fallback", name.take(1).uppercase())
            img.parentElement?.replaceChild(fallback, img)
        })
        return img
    }
    return el("span", "team-logo team-logo-fallback", name.take(1).uppercase())
}

private fun votesView(poll: PollDto): HTMLElement {
    val total = poll.votesHome + poll.votesDraw + poll.votesAway
    val grid = el("div", "votes-grid")
    listOf(
        Triple(outcomeChoiceLabel(poll, MatchOutcome.HOME_WIN), poll.votesHome, MatchOutcome.HOME_WIN),
        Triple(outcomeChoiceLabel(poll, MatchOutcome.DRAW), poll.votesDraw, MatchOutcome.DRAW),
        Triple(outcomeChoiceLabel(poll, MatchOutcome.AWAY_WIN), poll.votesAway, MatchOutcome.AWAY_WIN)
    ).forEach { (label, votes, outcome) ->
        val percent = if (total == 0) 0 else votes * 100 / total
        val isMine = poll.myPrediction == outcome
        val item = el("div", if (isMine) "vote-item mine-choice" else "vote-item")
        val head = el("div", "vote-head")
        val labelWrap = el("span", "vote-label-wrap")
        labelWrap.appendChild(el("span", "vote-label", label))
        if (isMine) {
            labelWrap.appendChild(el("span", "my-vote-badge", "Ваш голос"))
        }
        head.appendChild(labelWrap)
        head.appendChild(el("strong", text = "$votes · $percent%"))
        val track = el("div", "vote-track")
        val fill = el("div", if (isMine) "vote-fill mine" else "vote-fill")
        fill.style.width = "$percent%"
        track.appendChild(fill)
        item.appendChild(head)
        item.appendChild(track)
        grid.appendChild(item)
    }
    return grid
}

internal fun errorMessage(error: Throwable): String {
    val message = error.message.orEmpty()
    return when {
        message.contains("Invalid credentials", ignoreCase = true) -> "Неверный логин или пароль"
        message.contains("Failed to convert request body", ignoreCase = true) ||
            message.contains("RegisterRequest", ignoreCase = true) ->
            "Сервер устарел или перезапускается. Обновите страницу и попробуйте снова"
        message.contains("Failed to fetch", ignoreCase = true) -> "Не удалось связаться с сервером"
        message.contains("NetworkError", ignoreCase = true) -> "Ошибка сети. Проверьте подключение к интернету"
        message.startsWith("HTTP ") -> "Сервер вернул ошибку: $message"
        message.isBlank() -> "Произошла неизвестная ошибка"
        else -> message
    }
}

private fun clearFlash() {
    AppState.error = null
    AppState.notice = null
}

private const val BOOT_LOADER_MIN_MS = 1500.0

/** After auth: show ball loader, preload all leagues' polls/ratings, then enter app (≥1.5s). */
private suspend fun enterAppWithBootLoader() {
    activatePendingPredictions()
    showBootLoader()
    val started = Date.now()
    runCatching { preloadLineData() }
        .onFailure { console.error(it) }
    val elapsed = Date.now() - started
    if (elapsed < BOOT_LOADER_MIN_MS) {
        delay((BOOT_LOADER_MIN_MS - elapsed).toLong())
    }
    render()
    if (NOTIFICATIONS_UI_ENABLED && AppState.user != null && AppState.user?.mustChangePassword != true) {
        WebPush.ensureEnabledInBackground()
    }
}

private fun showBootLoader() {
    val root = document.getElementById("root") as HTMLElement
    root.innerHTML = ""
    val screen = el("div", "boot-loader")
    screen.setAttribute("role", "status")
    screen.setAttribute("aria-live", "polite")
    screen.setAttribute("aria-label", "Загрузка линии")
    val track = el("div", "boot-loader-track")
    track.setAttribute("aria-hidden", "true")
    track.innerHTML = BOOT_LOADER_SCENE_HTML
    screen.appendChild(track)
    screen.appendChild(el("p", "boot-loader-caption", "Загружаем линию…"))
    root.appendChild(screen)
}

/** Rolling football while line data preloads. */
private val BOOT_LOADER_SCENE_HTML = """
<div class="boot-chase">
  <img class="boot-football" src="boot/football.png" width="256" height="256" alt=""/>
</div>
""".trimIndent()

private suspend fun preloadLineData() {
    if (AppState.leagues.isEmpty()) {
        AppState.leagues = ApiClient.leagues()
    }
    coroutineScope {
        val pollsJob = async { ApiClient.polls() }
        val overallJob = async { ApiClient.ratings() }
        val perLeagueJobs = AppState.leagues.map { league ->
            async { league.code to ApiClient.ratings(league = league.code) }
        }
        val allPolls = pollsJob.await()
        AppState.pollsCache = allPolls.groupBy { it.league.name }
        val confirmedByServer = allPolls.filter { it.myPrediction != null }.map { it.id }.toSet()
        AppState.confirmedPredictions = AppState.confirmedPredictions - confirmedByServer
        val ratings = mutableMapOf<String, List<RatingEntryDto>>()
        ratings[ratingCacheKey(null, null)] = overallJob.await()
        perLeagueJobs.awaitAll().forEach { (code, rows) ->
            ratings[ratingCacheKey(code, null)] = rows
        }
        AppState.ratingsCache = ratings
    }
    AppState.lineDataReady = true
}

private fun activatePendingPredictions() {
    val user = AppState.user ?: return
    PendingPredictions.activate(
        userId = user.id,
        submit = { pending ->
            ApiClient.placePrediction(pending.pollId, pending.choice)
        },
        submitted = { pending ->
            AppState.confirmedPredictions =
                AppState.confirmedPredictions + (pending.pollId to pending.choice)
            AppState.notice = "Прогноз сохранён"
            AppState.error = null
            refreshVisiblePollCard(pending.pollId)
            scope.launch {
                runCatching { ApiClient.getPoll(pending.pollId) }
                    .onSuccess { fresh ->
                        AppState.confirmedPredictions = AppState.confirmedPredictions - pending.pollId
                        putPollInCache(fresh)
                        refreshVisiblePollCard(pending.pollId)
                    }
                    .onFailure {
                        AppState.notice = "Прогноз сохранён, но данные карточки пока не обновились"
                        if (AppState.page == Page.POLLS) render()
                    }
            }
        },
        failed = { pending, error ->
            AppState.confirmedPredictions = AppState.confirmedPredictions - pending.pollId
            AppState.error = errorMessage(error)
            AppState.notice = null
            if (AppState.page == Page.POLLS) {
                render()
            }
        }
    )
}

private fun putPollInCache(poll: PollDto) {
    val key = poll.league.name
    val current = AppState.pollsCache[key].orEmpty()
    val next = if (current.any { it.id == poll.id }) {
        current.map { if (it.id == poll.id) poll else it }
    } else {
        current + poll
    }
    AppState.pollsCache = AppState.pollsCache + (key to next)
    refreshNextMatchWidget?.invoke()
}

private fun refreshVisiblePollCard(pollId: Long) {
    val card = document.querySelector("[data-poll-id='$pollId']") as? HTMLElement ?: return
    val poll = AppState.pollsCache.values.asSequence().flatten().firstOrNull { it.id == pollId } ?: return
    val parent = card.parentElement ?: return
    PollCardCountdownViews.clear(pollId)
    parent.replaceChild(pollCard(poll), card)
}

private fun invalidateLineCaches() {
    AppState.pollsCache = emptyMap()
    AppState.ratingsCache = emptyMap()
    AppState.lineDataReady = false
}

private fun clearSessionCaches() {
    invalidateLineCaches()
    AppState.leagues = emptyList()
    AppState.selectedLeague = "EPL"
    AppState.selectedRound = null
    AppState.votedOnly = false
    AppState.closedOnly = false
    AppState.selectedRatingLeague = null
    AppState.selectedRatingRound = null
    AppState.confirmedPredictions = emptyMap()
    AppState.unreadNotifications = 0
}

private fun userBadge(displayName: String, avatarIcon: String?): HTMLElement {
    val wrap = el("div", "user-badge")
    wrap.appendChild(avatarNode(avatarIcon))
    wrap.appendChild(el("span", "user-name", displayName))
    return wrap
}

private fun avatarNode(avatarIcon: String?): HTMLElement {
    val img = document.createElement("img") as HTMLImageElement
    img.className = "avatar"
    img.src = achievementImageUrl(avatarIcon?.takeIf { it.isNotBlank() } ?: DEFAULT_AVATAR_ICON)
    img.alt = ""
    return img
}

internal fun el(tag: String, className: String? = null, text: String? = null): HTMLElement {
    val node = document.createElement(tag) as HTMLElement
    if (className != null) node.className = className
    if (text != null) node.textContent = text
    return node
}

/**
 * Creates a button. Press highlight (`.is-pressed`) is global — see [installButtonPressHighlight].
 * Prefer this helper (or plain `el("button", ...)`) so new controls inherit the same glow/fade.
 */
internal fun button(label: String, className: String, onClick: () -> Unit): HTMLElement {
    val btn = el("button", className, label)
    btn.addEventListener("click", {
        // Nav / filter remount the button; wait for press fade-out first.
        // League tabs stay in DOM and toggle .active immediately (CSS crossfade).
        if (buttonRemountsOnClick(btn)) {
            afterPressHighlightFade { onClick() }
        } else {
            onClick()
        }
    })
    return btn
}

private const val PRESS_HIGHLIGHT_FADE_MS = 300

/** True when click replaces this button in the DOM (full render or local paint). */
private fun buttonRemountsOnClick(btn: HTMLElement): Boolean {
    val c = btn.classList
    return c.contains("nav") ||
        c.contains("filter-chip") ||
        c.contains("auth-tab") ||
        c.contains("next-match")
}

private fun afterPressHighlightFade(action: () -> Unit) {
    window.setTimeout({ action() }, PRESS_HIGHLIGHT_FADE_MS)
}

/** Document-level press highlight for every `button` (current and future). */
private fun installButtonPressHighlight() {
    fun clearPressed() {
        val nodes = document.querySelectorAll("button.is-pressed")
        for (i in 0 until nodes.length) {
            (nodes.item(i) as? HTMLElement)?.classList?.remove("is-pressed")
        }
    }

    document.addEventListener("pointerdown", { event ->
        val pe = event.asDynamic()
        if (pe.button != null && pe.button != 0) return@addEventListener
        val target = event.target as? HTMLElement ?: return@addEventListener
        val btn = target.closest("button") as? HTMLElement ?: return@addEventListener
        if (btn.asDynamic().disabled == true) return@addEventListener
        btn.classList.add("is-pressed")
    })
    // pointerup before click: drop .is-pressed so CSS fade-out can run while the node still exists
    listOf("pointerup", "pointercancel", "lostpointercapture").forEach { name ->
        document.addEventListener(name, { clearPressed() }, true)
    }
}

internal fun input(type: String, placeholder: String, value: String = ""): HTMLInputElement {
    val input = document.createElement("input") as HTMLInputElement
    input.type = type
    input.placeholder = placeholder
    input.value = value
    input.className = "input"
    return input
}

internal fun field(label: String, input: HTMLInputElement): HTMLElement {
    val wrap = el("label", "field")
    wrap.appendChild(el("span", text = label))
    wrap.appendChild(input)
    return wrap
}
